import { ReactNode } from 'react'
import { motion } from 'framer-motion'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'

const EASE = [0.22, 1, 0.36, 1] as const

export interface Step {
  title: string
  description: string
}

interface ProductHowItWorksProps {
  eyebrow?: string
  title: ReactNode
  body?: string
  steps: Step[]
}

export default function ProductHowItWorks({
  eyebrow = 'How it works',
  title,
  body,
  steps,
}: ProductHowItWorksProps) {
  return (
    <Section tone="default" density="spacious">
      <Container size="xl">
        <div className="max-w-3xl">
          <Eyebrow tone="accent">{eyebrow}</Eyebrow>
          <Heading as="h2" size="display-lg" className="mt-5">
            {title}
          </Heading>
          {body && (
            <Text size="xl" tone="secondary" className="mt-6">
              {body}
            </Text>
          )}
        </div>

        <div className="mt-16 grid gap-6 lg:grid-cols-3 lg:gap-8">
          {steps.map((step, i) => (
            <motion.div
              key={step.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, ease: EASE, delay: i * 0.06 }}
              className="relative rounded-3xl border border-line-subtle bg-surface-subtle p-8 lg:p-10"
            >
              <div className="flex items-center gap-4">
                <div className="text-display-md font-medium text-brand-900/30 tabular-nums">
                  {String(i + 1).padStart(2, '0')}
                </div>
                <div className="h-px flex-grow bg-line" />
              </div>
              <h3 className="mt-6 text-display-sm font-medium text-ink-primary">
                {step.title}
              </h3>
              <Text size="md" tone="secondary" className="mt-3">
                {step.description}
              </Text>
            </motion.div>
          ))}
        </div>
      </Container>
    </Section>
  )
}
